import { Fragment } from 'react';
import Image from 'next/image';

import { Box, CircularProgress, CircularProgressLabel, Heading, VisuallyHidden } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { StandardHeroSection } from '~/components/standard-hero';
import { useI18n } from '~/hooks/use-i18n';

export type HeroSectionProps = {
  title: string;
  blurb: string;
  stats: Array<{ title: string; text: string }>;
};

export function HeroSection({ title, blurb, stats }: HeroSectionProps) {
  const { isRtl } = useI18n();
  const statColors = ['accent.lime', 'accent.purple', 'accent.sky.500'] as const;
  return (
    <>
      <StandardHeroSection
        backgroundImageSlot={
          <Box inset={0} pointerEvents='none' position='absolute'>
            <Box
              inset={0}
              position='absolute'
              left={
                isRtl
                  ? 'unset'
                  : {
                      md: '50%',
                    }
              }
              right={
                isRtl
                  ? {
                      md: '50%',
                    }
                  : 'unset'
              }
              transform={{
                md: isRtl ? 'translateX(50%)' : 'translateX(-50%)',
              }}
              width={{
                base: '1440px',
              }}
            >
              <Image
                alt=''
                loading='eager'
                sizes='1440px'
                src='/images/backgrounds/line-pattern-left.png'
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center',
                  filter: 'opacity(0.25)',
                }}
                fill
                priority
              />
              <Image
                alt=''
                loading='eager'
                sizes='1440px'
                src='/images/backgrounds/line-pattern-right.png'
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center',
                  filter: 'opacity(0.25)',
                }}
                fill
                priority
              />
            </Box>
          </Box>
        }
        containerProps={{
          bgGradient: 'linear(180deg ,#333AB9 0%, #0F123D 100%)',
          minHeight: '400px',
          color: 'black',
          alignItems: 'center',
        }}
        contentSlot={
          <Box textAlign='center'>
            <Heading
              as='h1'
              color='white'
              fontWeight='500 !important'
              mb={6}
              size={{
                base: 'marketing-4xl',
                md: 'marketing-5xl',
              }}
              whiteSpace={{
                base: 'pre-wrap',
                md: 'unset',
              }}
            >
              {title}
            </Heading>
            <Heading
              as='p'
              color='accent.lime'
              px={2}
              size={{
                base: 'marketing-lg',
                md: 'marketing-2xl',
              }}
            >
              {blurb}
            </Heading>
          </Box>
        }
      />
      <Box as='section' bgColor='blue.800' paddingBottom={10} paddingTop={8}>
        <ContentContainer color='white'>
          <Box
            as='dl'
            display='grid'
            gap={4}
            alignItems={{
              base: 'flex-start',
              xl: 'center',
            }}
            flexDir={{
              base: 'column',
              md: 'row',
            }}
            gridTemplateColumns={{
              base: 'auto 1fr',
              md: 'auto 1fr auto 1fr auto 1fr',
            }}
            sx={{
              '& > dd + dt': {
                marginLeft: {
                  md: 6,
                },
              },
            }}
          >
            {stats.map((stat, idx) => {
              const value = stat.title.trim().match(/(\d+)%/)?.[1];
              const statColor = statColors[idx % statColors.length];
              return (
                <Fragment key={idx}>
                  <Box as='dt'>
                    <CircularProgress
                      aria-hidden='true'
                      aria-label={stat.title}
                      color={statColor}
                      size='64px'
                      thickness='3px'
                      trackColor='blue.700'
                      value={typeof value === 'string' ? Number(value) : 0}
                      display={{
                        base: 'block',
                        md: 'none',
                      }}
                    >
                      <Heading as={CircularProgressLabel} fontWeight='500' size='marketing-sm'>
                        {stat.title}
                      </Heading>
                    </CircularProgress>
                    <CircularProgress
                      aria-hidden='true'
                      aria-label={stat.title}
                      color={statColor}
                      size='88px'
                      thickness='3px'
                      trackColor='blue.700'
                      value={typeof value === 'string' ? Number(value) : 0}
                      display={{
                        base: 'none',
                        md: 'block',
                      }}
                    >
                      <Heading as={CircularProgressLabel} fontWeight='500' size='marketing-xl'>
                        {stat.title}
                      </Heading>
                    </CircularProgress>
                    <VisuallyHidden>{stat.title}</VisuallyHidden>
                  </Box>
                  <HeadingWithEmphasis
                    text={stat.text}
                    emphasisProps={{
                      color: statColor,
                      fontWeight: '600',
                    }}
                    headingProps={{
                      as: 'dd',
                      size: 'marketing-md',
                      fontWeight: 'normal',
                      fontFamily: 'body',
                      alignSelf: 'center',
                    }}
                  />
                </Fragment>
              );
            })}
          </Box>
        </ContentContainer>
      </Box>
    </>
  );
}
